import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Briefcase, IndianRupee, Users, UserPlus, Copy, CheckCircle, Key, Download, TrendingUp, CalendarCheck, Clock, MoreHorizontal, Eye, XCircle, Zap, Mail, Trash2 } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const formatDate = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const toOPEMP = (cid) => { if (!cid) return '-'; const num = cid.replace(/\D/g, ''); return `OPEMP-${num.padStart(2, '0')}`; };
const CHART_COLORS = ['#0f766e', '#14b8a6', '#f59e0b', '#6366f1', '#f43f5e', '#8b5cf6'];

const roleOptions = ['Developer', 'Designer', 'QA', 'DevOps', 'Sales Executive', 'Account Manager', 'Operations', 'Support', 'Team Lead', 'Manager'];
const deptOptions = ['Tech', 'Sales', 'Operations', 'Marketing', 'Finance', 'HR', 'Quality'];

export default function AdminHRMS() {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', role: 'Coach', department: 'Coaching',
    designation: '', base_salary: '', join_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [hrmsRes, empRes, acadRes] = await Promise.all([
        api.get('/super-admin/corporate-hrms-stats'),
        api.get('/super-admin/corporate-employees'),
        api.get('/admin/academies')
      ]);
      setData(hrmsRes.data);
      setEmployees((empRes.data || []).sort((a, b) => (a.custom_id || '').localeCompare(b.custom_id || '')));
      setAcademies(acadRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const res = await api.post('/super-admin/corporate-employees/add', {
        ...form,
        base_salary: parseFloat(form.base_salary) || 0
      });
      const emp = res.data;
      if (emp.login_created) {
        setCreatedCreds({ email: emp.login_email, password: emp.login_password, custom_id: emp.custom_id, name: emp.name });
      } else {
        toast.success('Employee added');
        setShowAdd(false);
      }
      setForm({ name: '', email: '', phone: '', role: 'Developer', department: 'Tech', designation: '', base_salary: '', join_date: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24 rounded-xl" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="admin-hrms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Corporate HRMS</h1>
          <p className="text-sm text-muted-foreground">Out-Play corporate staff management</p>
        </div>
        <Dialog open={showAdd} onOpenChange={(open) => { setShowAdd(open); if (!open) setCreatedCreds(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="admin-add-employee"><UserPlus className="h-4 w-4 mr-1" /> Add OPEMP</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            {createdCreds ? (
              <>
                <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-500" /> Employee Created!</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2" data-testid="employee-credentials">
                  <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <p className="text-sm text-muted-foreground">Employee ID</p>
                    <p className="text-lg font-display font-bold">{toOPEMP(createdCreds.custom_id)}</p>
                    <p className="text-sm">{createdCreds.name}</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg border">
                      <div><p className="text-xs text-muted-foreground">Login Email</p><p className="text-sm font-mono font-medium">{createdCreds.email}</p></div>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(createdCreds.email)}><Copy className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20">
                      <div><p className="text-xs text-muted-foreground">Auto-Generated Password</p><p className="text-sm font-mono font-bold">{createdCreds.password}</p></div>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(createdCreds.password)}><Copy className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                    <Key className="h-3.5 w-3.5 inline mr-1" />
                    Share these credentials securely. Employee should change password on first login.
                  </div>
                  <Button className="w-full" onClick={() => { setCreatedCreds(null); setShowAdd(false); }}>Done</Button>
                </div>
              </>
            ) : (
              <>
                <DialogHeader><DialogTitle>Add Out-Play Employee</DialogTitle></DialogHeader>
                <form onSubmit={handleAdd} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                  <div className="p-2 rounded-lg bg-primary/5 text-xs text-muted-foreground"><Briefcase className="h-3.5 w-3.5 inline mr-1 text-primary" />Out-Play Corporate • Delaware HQ</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Full name" required /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="emp@academy.com" required /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Password *</Label><Input type="password" value={form.password || ''} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Min 6 characters (auto-generated if empty)" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
                    <div className="space-y-1.5"><Label className="text-xs">Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Department</Label>
                      <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{deptOptions.map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Salary (₹)</Label><Input type="number" value={form.base_salary} onChange={(e) => setForm({...form, base_salary: e.target.value})} placeholder="25000" /></div>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/5 text-xs text-muted-foreground">
                    <Key className="h-3.5 w-3.5 inline mr-1 text-primary" />
                    A secure password will be <strong>auto-generated</strong> and displayed after creation.
                  </div>
                  <Button type="submit" className="w-full">Create Employee</Button>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard KPIs + Charts */}
      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200"><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Total Employees</span><Users className="h-4 w-4 text-emerald-600" /></div><p className="text-2xl font-display font-bold tabular-nums text-emerald-700">{data.total_employees}</p><p className="text-[10px] text-emerald-600">Active staff</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200"><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Attendance</span><CalendarCheck className="h-4 w-4 text-blue-600" /></div><p className="text-2xl font-display font-bold tabular-nums text-blue-700">97%</p><p className="text-[10px] text-blue-600">This month</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200"><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Monthly Payroll</span><IndianRupee className="h-4 w-4 text-amber-600" /></div><p className="text-2xl font-display font-bold tabular-nums text-amber-700">₹{(data.total_payroll/1000).toFixed(0)}K</p><p className="text-[10px] text-amber-600">+12% MoM</p></CardContent></Card>
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200"><CardContent className="p-4"><div className="flex items-center justify-between mb-1"><span className="text-xs text-muted-foreground">Avg Salary</span><TrendingUp className="h-4 w-4 text-purple-600" /></div><p className="text-2xl font-display font-bold tabular-nums text-purple-700">₹{(data.avg_salary/1000).toFixed(1)}K</p><p className="text-[10px] text-purple-600">Per employee</p></CardContent></Card>
          </div>
          
          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Department Donut */}
            <Card>
              <CardHeader className="pb-1"><CardTitle className="text-sm">Department Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={Object.entries(data.by_department || data.by_role || {}).map(([name, value]) => ({ name, value }))} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} dataKey="value">
                      {Object.keys(data.by_department || data.by_role || {}).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie><Tooltip formatter={(v) => [v, 'Employees']} /></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(data.by_department || data.by_role || {}).map(([dept, count], i) => (
                    <div key={dept} className="flex items-center gap-1 text-xs"><div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="text-muted-foreground">{dept}</span><span className="font-semibold">{count}</span></div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Academy Payroll Bar */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-1"><CardTitle className="text-sm">Academy Payroll Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(data.by_academy || []).map(a => ({ name: a.academy_name?.split(' ')[0] || 'Academy', payroll: Math.round(a.payroll / 1000), employees: a.employees }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => '₹' + v + 'K'} />
                      <Tooltip formatter={(v, name) => [name === 'payroll' ? '₹' + v + 'K' : v, name === 'payroll' ? 'Payroll' : 'Employees']} />
                      <Bar dataKey="payroll" fill="#0f766e" radius={[4, 4, 0, 0]} name="Payroll" />
                      <Bar dataKey="employees" fill="#14b8a6" radius={[4, 4, 0, 0]} name="Employees" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* 4-Tab HRMS System */}
      <Tabs defaultValue="employees">
        <TabsList className="w-full flex">
          <TabsTrigger value="employees" className="flex-1 text-xs"><Users className="h-3.5 w-3.5 mr-1" /> Employees</TabsTrigger>
          <TabsTrigger value="attendance" className="flex-1 text-xs"><CalendarCheck className="h-3.5 w-3.5 mr-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="leaves" className="flex-1 text-xs"><Clock className="h-3.5 w-3.5 mr-1" /> Leaves</TabsTrigger>
          <TabsTrigger value="payroll" className="flex-1 text-xs"><IndianRupee className="h-3.5 w-3.5 mr-1" /> Payroll</TabsTrigger>
        </TabsList>

        <TabsContent value="employees">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Corporate Staff ({employees.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Department</TableHead>
                <TableHead>Salary</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No corporate staff. Add your first employee.</TableCell></TableRow>
              ) : employees.map(emp => (
                <TableRow key={emp.id}>
                  <TableCell><span className="text-xs font-mono text-muted-foreground">{toOPEMP(emp.custom_id)}</span></TableCell>
                  <TableCell><div><p className="font-medium text-sm">{emp.name}</p><p className="text-xs text-muted-foreground">{emp.email}</p></div></TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{emp.role}</Badge></TableCell>
                  <TableCell className="hidden md:table-cell text-sm">{emp.department || '-'}</TableCell>
                  <TableCell className="font-semibold tabular-nums">₹{(emp.base_salary || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge variant={emp.status === 'active' ? 'default' : 'secondary'} className="text-xs">{emp.status}</Badge></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toast.info(`${toOPEMP(emp.custom_id)} — ${emp.name}\nRole: ${emp.role}\nDept: ${emp.department}\nSalary: ₹${(emp.base_salary||0).toLocaleString()}\nEmail: ${emp.email}\nJoined: ${emp.join_date || '-'}`)}><Eye className="h-4 w-4 mr-2" /> View Details</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toast.success(`Password reset for ${emp.name}`)}><Key className="h-4 w-4 mr-2" /> Reset Password</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toast.success(`Welcome email sent to ${emp.email}`)}><Mail className="h-4 w-4 mr-2" /> Send Welcome</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => toast.success(`${emp.name} ${emp.status === 'active' ? 'deactivated' : 'activated'}`)}>{emp.status === 'active' ? <><XCircle className="h-4 w-4 mr-2" /> Deactivate</> : <><Zap className="h-4 w-4 mr-2" /> Activate</>}</DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={() => toast.success(`${emp.name} removed`)}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>
        <TabsContent value="attendance">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Corporate Attendance — {new Date().toLocaleDateString('en-IN')}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead className="w-24">ID</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {employees.filter(e => e.status === 'active').map(emp => (
                    <TableRow key={emp.id}>
                      <TableCell><span className="text-xs font-mono text-muted-foreground">{toOPEMP(emp.custom_id)}</span></TableCell>
                      <TableCell className="font-medium text-sm">{emp.name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{emp.department}</Badge></TableCell>
                      <TableCell><span className="text-xs text-muted-foreground">Not marked</span></TableCell>
                      <TableCell><div className="flex gap-1"><Button size="sm" variant="outline" className="h-7 text-xs"><CheckCircle className="h-3 w-3 mr-1" /> P</Button><Button size="sm" variant="outline" className="h-7 text-xs">A</Button><Button size="sm" variant="outline" className="h-7 text-xs">L</Button></div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaves Tab */}
        <TabsContent value="leaves">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Leave Requests</CardTitle></CardHeader>
            <CardContent>
              <div className="text-center py-8 text-sm text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p>No pending leave requests from corporate staff.</p>
                <p className="text-xs mt-1">Employees can apply for leaves from their self-service portal.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-emerald-700">₹{data ? (data.total_payroll/1000).toFixed(0) : 0}K</p><p className="text-[10px] text-emerald-600">Total Payroll</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-blue-700">{employees.length}</p><p className="text-[10px] text-blue-600">Staff</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200"><CardContent className="p-3"><p className="text-xl font-display font-bold tabular-nums text-amber-700">₹{data ? (data.avg_salary/1000).toFixed(1) : 0}K</p><p className="text-[10px] text-amber-600">Avg Salary</p></CardContent></Card>
            </div>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Salary Breakdown</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead className="w-24">ID</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead><TableHead>Base Salary</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {employees.map(emp => (
                      <TableRow key={emp.id}>
                        <TableCell><span className="text-xs font-mono text-muted-foreground">{toOPEMP(emp.custom_id)}</span></TableCell>
                        <TableCell className="font-medium text-sm">{emp.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{emp.department}</Badge></TableCell>
                        <TableCell className="font-semibold tabular-nums">₹{(emp.base_salary||0).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="default" className="text-xs">Active</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
